import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Box,
  Typography,
  Avatar,
} from '@mui/material';
import {
  Edit as EditIcon,
  Close as CloseIcon,
  CloudQueue as CloudIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { filterInput } from '../utils/inputSanitization';

const AccountRenameModal = ({ open, onClose, account, onSuccess }) => {
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && account) {
      setNewName(account.accountName || '');
      setError('');
    }
  }, [open, account]);

  const handleSubmit = async () => {
    if (!newName || !newName.trim()) {
      setError('Account name is required');
      return;
    }

    if (newName.trim() === account.accountName) {
      setError('Please enter a different name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiService.renameAWSAccount(account.accountId, newName.trim());

      if (response && response.success) {
        toast.success(`Account renamed to "${newName.trim()}" successfully`);
        onSuccess && onSuccess();
        onClose();
      } else {
        throw new Error(response?.message || 'Failed to rename account');
      }
    } catch (err) {
      console.error('Failed to rename account:', err);
      setError(err.message || 'Failed to rename account');
      toast.error(err.message || 'Failed to rename account');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleSubmit();
    }
  };

  if (!account) return null;

  // Minimalist input style
  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'Metropolis, sans-serif',
      backgroundColor: '#FFFFFF',
      '& fieldset': {
        borderColor: '#E5E5E5',
      },
      '&:hover fieldset': {
        borderColor: '#F39C12',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#F39C12',
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'Metropolis, sans-serif',
      color: '#666666',
      '&.Mui-focused': {
        color: '#F39C12',
      },
    },
    '& .MuiFormHelperText-root': {
      fontFamily: 'Metropolis, sans-serif',
      color: '#E74C3C',
    },
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          border: '1px solid #E5E5E5',
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: '#FEF9E7',
          borderBottom: '1px solid #E5E5E5',
          fontFamily: 'Bitter, Georgia, serif',
          fontWeight: 600,
          fontSize: '1.25rem',
          color: '#253E66',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          position: 'relative',
        }}
      >
        <Avatar sx={{ backgroundColor: '#F39C12', width: 40, height: 40 }}>
          <EditIcon />
        </Avatar>
        Rename AWS Account
        <IconButton
          onClick={onClose}
          disabled={loading}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: '#666666',
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E5E5',
            '&:hover': { backgroundColor: '#F9FBFD' },
            '&:disabled': { opacity: 0.5 },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 3, backgroundColor: '#FFFFFF' }}>
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              backgroundColor: '#F9FBFD',
              borderRadius: '6px',
              p: 1.5,
              border: '1px solid #E5E5E5',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontSize: '0.9rem',
                color: '#253E66',
                fontWeight: 600,
              }}
            >
              {account.accountName || 'Unnamed Account'}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                color: '#666666',
              }}
            >
              {account.accountId}
            </Typography>
          </Box>
        </Box>

        <TextField
          autoFocus
          fullWidth
          label="Account Name"
          value={newName}
          onChange={(e) => {
            setNewName(filterInput(e.target.value, 'GENERAL_TEXT'));
            setError('');
          }}
          onKeyPress={handleKeyPress}
          disabled={loading}
          error={!!error}
          helperText={error}
          placeholder="Enter new account name"
          sx={inputStyle}
        />
        </motion.div>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, backgroundColor: '#FFFFFF', gap: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            textTransform: 'none',
            color: '#666666',
            borderColor: '#E5E5E5',
            borderRadius: '6px',
            '&:hover': {
              borderColor: '#999999',
              backgroundColor: '#F9FBFD',
            },
            '&:disabled': {
              color: '#999999',
            },
          }}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || !newName.trim()}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <EditIcon />}
          sx={{
            backgroundColor: '#F39C12',
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '6px',
            px: 3,
            '&:hover': {
              backgroundColor: '#E67E22',
            },
            '&.Mui-disabled': {
              backgroundColor: '#E5E5E5',
              color: '#999999',
            },
          }}
        >
          {loading ? 'Renaming...' : 'Rename'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountRenameModal;
